import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts'
import { TrendIndicator } from './TrendIndicator'
import type { TrendDirection } from '@/types'

interface SleepData {
  date: string
  value: number // hours
  quality?: number // percentage 0-100
}

interface SleepChartProps {
  data: SleepData[]
  goalHours?: number
  trend?: {
    direction: TrendDirection
    changePercent?: number
  }
  isLoading?: boolean
}

function formatDate(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function formatShortDate(dateStr: string) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function formatHours(hours: number) {
  const h = Math.floor(hours)
  const m = Math.round((hours - h) * 60)
  return m > 0 ? `${h}h ${m}m` : `${h}h`
}

function ChartSkeleton() {
  return (
    <div className="hearth-card p-5">
      <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
      <div className="mt-4 h-64 bg-gray-100 rounded animate-pulse" />
    </div>
  )
}

export function SleepChart({ data, goalHours = 8, trend, isLoading }: SleepChartProps) {
  if (isLoading) {
    return <ChartSkeleton />
  }

  const average = data.length > 0
    ? data.reduce((sum, d) => sum + d.value, 0) / data.length
    : 0

  const averageQuality = data.filter(d => d.quality !== undefined).length > 0
    ? Math.round(
        data.filter(d => d.quality !== undefined).reduce((sum, d) => sum + (d.quality ?? 0), 0) /
        data.filter(d => d.quality !== undefined).length
      )
    : undefined

  return (
    <div className="hearth-card p-5">
      <div className="mb-4 flex flex-row items-start justify-between gap-3">
        <div>
          <h3 className="text-[15px] font-semibold text-gray-900">Sleep</h3>
          <p className="mt-0.5 text-[13px] text-gray-500">
            Average <span className="font-rounded font-medium tabular-nums">{formatHours(average)}</span> a night
            {averageQuality !== undefined && ` · Quality ${averageQuality}%`}
          </p>
        </div>
        {trend && (
          <TrendIndicator
            trend={trend.direction}
            changePercent={trend.changePercent}
            showLabel
            size="sm"
          />
        )}
      </div>
      <div>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="var(--color-gray-200)" />
              <XAxis
                dataKey="date"
                tickFormatter={formatShortDate}
                tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
                axisLine={{ stroke: 'var(--color-gray-200)' }}
                tickLine={false}
              />
              <YAxis
                domain={[0, 12]}
                tick={{ fontSize: 12, fill: 'var(--color-gray-500)' }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(value) => `${value}h`}
              />
              <Tooltip
                content={({ active, payload }) => {
                  if (active && payload && payload.length) {
                    const data = payload[0].payload as SleepData
                    return (
                      <div className="rounded-lg border border-gray-200 bg-surface px-3 py-2 shadow-lg">
                        <p className="text-xs text-gray-500">{formatDate(data.date)}</p>
                        <p className="font-rounded text-sm font-semibold tabular-nums text-gray-900">
                          {formatHours(data.value)}
                        </p>
                        {data.quality !== undefined && (
                          <p className="text-xs text-gray-500">
                            Quality: {data.quality}%
                          </p>
                        )}
                        {goalHours && (
                          <p className="text-xs text-gray-500">
                            {data.value >= goalHours
                              ? 'Goal reached!'
                              : `${Math.round((data.value / goalHours) * 100)}% of ${goalHours}h goal`}
                          </p>
                        )}
                      </div>
                    )
                  }
                  return null
                }}
              />
              {goalHours && (
                <ReferenceLine
                  y={goalHours}
                  stroke="#7C6FA8"
                  strokeDasharray="5 5"
                  label={{
                    value: 'Goal',
                    position: 'right',
                    fill: '#7C6FA8',
                    fontSize: 12,
                  }}
                />
              )}
              <Bar
                dataKey="value"
                fill="#7C6FA8"
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="flex h-64 items-center justify-center">
            <p className="text-sm text-gray-500">Sleep readings appear here as they sync.</p>
          </div>
        )}
      </div>
    </div>
  )
}
